import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { useNavigate } from "react-router";

import { searchCompanies } from "../../api/companies";
import type { CompanyListItem } from "../../api/companies";
import { AdviceCard } from "../../components/AdviceCard";
import { Button } from "../../components/Button";
import { CompanyAvatar } from "../../components/CompanyAvatar";
import { SearchIcon } from "../../components/icons";
import { StatusChip } from "../../components/StatusChip";
import { nl } from "../../i18n/nl";
import { COMPANIES_KEY } from "../companies/queries";
import { AddCompanyForm } from "./AddCompanyForm";
import styles from "./search.module.css";

const t = nl.search;
export const SEARCH_DEBOUNCE_MS = 200;

function foldChar(char: string): string {
  return (char.normalize("NFKD").replace(/\p{M}/gu, "").charAt(0) || char).toLowerCase();
}

function fold(text: string): string[] {
  return Array.from(text, foldChar);
}

/** Marks the start of every word that one of the query words is a prefix of (accent-insensitive). */
export function highlight(name: string, query: string): ReactNode {
  const words = query
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean)
    .map((w) => fold(w).join(""));
  const chars = Array.from(name);
  const folded = fold(name);
  const marked = chars.map(() => false);
  chars.forEach((_, i) => {
    const startOfWord = i === 0 || !/[\p{L}\p{N}]/u.test(chars[i - 1] ?? "");
    if (!startOfWord) return;
    for (const word of words) {
      if (folded.slice(i, i + word.length).join("") === word) {
        for (let j = i; j < i + word.length; j++) marked[j] = true;
      }
    }
  });
  // Group consecutive characters with the same mark into one string or <mark>.
  const parts: ReactNode[] = [];
  let start = 0;
  for (let i = 1; i <= chars.length; i++) {
    if (i === chars.length || marked[i] !== marked[start]) {
      const text = chars.slice(start, i).join("");
      parts.push(marked[start] ? <mark key={start}>{text}</mark> : text);
      start = i;
    }
  }
  return parts;
}

function useDebounced(value: string): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebounced(value);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value]);
  return debounced;
}

function Preview({ company, onOpen }: { company: CompanyListItem; onOpen: () => void }) {
  const { kind } = company.advice;
  return (
    <aside className={styles.preview} aria-label={company.name}>
      <StatusChip status={company.status} />
      <h3 className={styles.previewTitle}>{company.name}</h3>
      <p className={styles.muted}>
        {[company.municipality, company.distance_km !== null ? nl.companies.distance(company.distance_km) : null]
          .filter(Boolean)
          .join(" · ")}
      </p>
      <AdviceCard status={company.status} title={nl.companies.adviceTitles[kind]} text={nl.companies.adviceTexts[kind]} />
      <div>
        <Button variant="primary" onClick={onOpen}>
          {t.open}
        </Button>
      </div>
    </aside>
  );
}

/** Live search with highlighting, keyboard navigation and a preview; "Bedrijf toevoegen" below. */
export function SearchPanel({ onNavigate, autoFocusInput = false }: { onNavigate?: () => void; autoFocusInput?: boolean }) {
  const id = useId();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [active, setActive] = useState(0);
  const [adding, setAdding] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const term = useDebounced(text.trim());

  // Opened to type right away. After a frame, so a dialog's own initial focus does not win.
  useEffect(() => {
    if (!autoFocusInput) return;
    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [autoFocusInput]);
  const results = useQuery({
    queryKey: [...COMPANIES_KEY, "search", term],
    queryFn: () => searchCompanies(term),
    enabled: term.length >= 2,
  });
  const items = term.length >= 2 ? (results.data ?? []) : [];
  const current = items[Math.min(active, items.length - 1)];

  function open(company: CompanyListItem) {
    onNavigate?.();
    void navigate(`/bedrijven/${company.id}`);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (items.length === 0) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActive((value) => (value + step + items.length) % items.length);
    } else if (event.key === "Enter" && current) {
      event.preventDefault();
      open(current);
    }
  }

  if (adding) {
    return (
      <AddCompanyForm
        initialName={text.trim()}
        onDone={() => {
          setAdding(false);
          onNavigate?.();
        }}
      />
    );
  }

  const listId = `${id}-results`;
  return (
    <div className={styles.panel}>
      <label className={styles.searchField} htmlFor={`${id}-input`}>
        <SearchIcon size={18} />
        <span className="sr-only">{t.label}</span>
        <input
          id={`${id}-input`}
          ref={inputRef}
          role="combobox"
          aria-expanded={items.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={current ? `${listId}-${current.id}` : undefined}
          aria-describedby={`${id}-hint`}
          autoComplete="off"
          placeholder={t.placeholder}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
      </label>
      <p id={`${id}-hint`} className={styles.muted}>
        {t.hint}
      </p>

      <div className={styles.body}>
        <div className={styles.resultsColumn}>
          {term.length >= 2 && (
            <p className={styles.groupTitle} aria-live="polite">
              {results.isFetching && items.length === 0
                ? t.searching
                : results.isError
                  ? t.error
                  : items.length === 0
                    ? t.none(term)
                    : t.results}
            </p>
          )}
          {items.length > 0 && (
            <div id={listId} role="listbox" aria-label={t.results} className={styles.results}>
              {items.map((company, index) => (
                <div
                  key={company.id}
                  id={`${listId}-${company.id}`}
                  role="option"
                  tabIndex={-1}
                  aria-selected={company.id === current?.id}
                  className={[styles.result, company.id === current?.id && styles.resultActive].filter(Boolean).join(" ")}
                  onMouseEnter={() => {
                    setActive(index);
                  }}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    open(company);
                  }}
                >
                  <CompanyAvatar id={company.id} name={company.name} hue={company.avatar_hue} status={company.status} />
                  <span className={styles.resultText}>
                    <span className={styles.resultName}>{highlight(company.name, term)}</span>
                    <span className={styles.muted}>
                      {nl.status[company.status]} · {highlight(company.municipality, term)}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
          <div className={styles.addRow}>
            <p className={styles.muted}>{t.addHint}</p>
            <Button
              onClick={() => {
                setAdding(true);
              }}
            >
              {t.add}
            </Button>
          </div>
        </div>
        {current && (
          <Preview
            company={current}
            onOpen={() => {
              open(current);
            }}
          />
        )}
      </div>
    </div>
  );
}
